import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../hooks";
import { selectProperty } from "../../states/slices/globalReducer";
import {
  CHEVRON_RIGHT,
  COAT_OF_ARMS,
  DOWNLOAD_ICON,
  GO_BACK,
  SIGNATURE,
  STAMP,
} from "../../assets";
import ButtonV2 from "../../shared/components/buttonV2";
import { useQuery } from "react-query";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { finalizeSearch, getJudicialDivisions } from "../../Services/services";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchHistoryTypes } from "../../types";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import moment from "moment";
import { getPlatformCharge } from "../../Services/admin.service";

function SearchDetails() {
  const property = useAppSelector(selectProperty);
  const [downloading, setDownloading] = useState(false);
  const [searchDetails, setSearchDetails] = useState<SearchHistoryTypes | null>(
    null,
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const pdfRef = useRef<HTMLElement | null>(null);
  const { data: judicialDivision } = useQuery(
    ["division", 1],
    () => getJudicialDivisions(1),
    {
      refetchOnWindowFocus: false,
    },
  );
  const { data: generalSettings } = useQuery(
    "generalSettings",
    getPlatformCharge,
    {
      refetchOnWindowFocus: false,
    },
  );
  const [searchParams] = useSearchParams();

  const reference = searchParams.get("reference") || "";

  const downloadPdf = async () => {
    setDownloading(true);
    const node = pdfRef.current;
    const dataUrl = await toPng(node!, { cacheBust: true });
    const pdf = new jsPDF("p", "pt", "a4");
    const imgProps = pdf.getImageProperties(dataUrl);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfheight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfheight);
    pdf.save("lispendes-certificate.pdf");
    setDownloading(false);
  };

  const handleShowSearchDetails = async () => {
    setLoading(true);
    const payload = {
      reference: reference,
      case_id: property?.id,
    };
    try {
      const response = await finalizeSearch(payload);
      if (response) {
        setSearchDetails(response?.data);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reference) {
      handleShowSearchDetails();
    }
  }, [reference]);

  return (
    <div className="p-3 sm:p-5">
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-xl sm:text-2xl">
          Search Detail
        </span>
        <div className="flex items-center gap-x-1 flex-wrap">
          <span
            onClick={() => window.history.back()}
            className="text-primary-50 cursor-pointer font-medium text-sm"
          >
            Dashboard
          </span>
          <img src={CHEVRON_RIGHT} alt="" className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="text-globalGold font-medium text-sm break-words">
            {property?.title || searchDetails?.title}
          </span>
        </div>
      </div>

      <div className="w-full flex items-center justify-center">
        <div className="w-full max-w-6xl px-2 sm:px-4 lg:px-8 xl:px-16 mt-8 sm:mt-16 rounded-xl">
          <div className="w-full bg-lightGold flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-4 rounded-t-xl gap-2">
            <div className="flex items-center gap-x-2 flex-1 min-w-0">
              <div
                onClick={() => navigate("/individual/search-history")}
                className="w-[40px] h-[40px] cursor-pointer rounded-xl border border-[#E9EAEB] flex items-center justify-center bg-white flex-shrink-0"
              >
                <img src={GO_BACK} className="w-[20px] h-[20px]" alt="" />
              </div>
              <span className="text-[#2A2E34] font-semibold text-sm sm:text-[16px] truncate">
                {property?.title || searchDetails?.title}
              </span>
            </div>
            <ButtonV2
              title={"Download"}
              image={DOWNLOAD_ICON}
              disabled={loading}
              loading={downloading}
              loaderColor="#fff"
              btnStyle={
                "flex items-center justify-center gap-x-1 bg-globalGold px-3 py-2 rounded-xl flex-row-reverse flex-shrink-0"
              }
              textStyle={"text-white font-medium text-sm"}
              handleClick={downloadPdf}
            />
          </div>
          <div className="w-full border border-[#E9EAEB] rounded-b-xl">
            {loading ? (
              <div className="flex items-center justify-center h-[50vh]">
                <Loader2 color="#9ca3af" size={30} className="animate-spin" />
              </div>
            ) : (
              <div
                className="px-4 sm:px-8 lg:px-14"
                // @ts-ignore
                ref={pdfRef}
              >
                <div className="flex flex-col sm:flex-row items-start mt-4 mb-4 gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={COAT_OF_ARMS}
                      alt="coa"
                      className="object-contain w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] rounded-full"
                    />
                  </div>
                  <div className="text-center flex-1 space-y-1 sm:space-y-2">
                    <h2 className="text-lg sm:text-xl lg:text-[25px] font-bold uppercase text-green-500">
                      ANAMBRA STATE GOVERNMENT
                    </h2>
                    <h2 className="text-lg sm:text-xl lg:text-[25px] font-bold uppercase">
                      LIS PENDENS SEARCH REPORT
                    </h2>
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold uppercase">
                      IN THE HIGH COURT OF ANAMBRA STATE OF NIGERIA
                    </h2>
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold uppercase">
                      IN THE{" "}
                      {property?.judicial_division?.name ||
                        searchDetails?.judicial_division?.name}
                    </h2>
                    <h3 className="text-sm sm:text-base lg:text-lg font-medium uppercase">
                      HOLDEN AT{" "}
                      {property?.judicial_division?.name ||
                        searchDetails?.judicial_division?.name}
                    </h3>
                  </div>
                </div>

                <div className="text-left space-y-2 sm:space-y-3">
                  <div className="space-y-1">
                    <div className="text-sm sm:text-base">
                      {searchDetails?.court_details || property?.court_details}
                    </div>
                    <div className="text-sm sm:text-base">
                      Judicial Division:{" "}
                      <span className="">
                        {/* {judicialDivision?.find(
                          (item: any) =>
                            item.id.toString() ===
                            (property?.judicial_division_id ||
                              searchDetails?.judicial_division_id)
                        )?.name || "N/A"} */}
                        {property?.judicial_division?.name ||
                          searchDetails?.judicial_division?.name}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm sm:text-base">
                    Title Registration No:{" "}
                    {property?.title_number || searchDetails?.title_number}
                  </div>
                  <div className="text-sm sm:text-base break-words">
                    Property Address:{" "}
                    {property?.address || searchDetails?.address}
                  </div>
                  {/* <div className="text-sm sm:text-base break-words">
                    Name of Title Owner:{" "}
                    {property?.owner_name || searchDetails?.owner_name || "-"}
                  </div> */}
                  <div className="text-sm sm:text-base break-words">
                    Name of Parties:{" "}
                    {property?.parties || searchDetails?.parties || "-"}
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm sm:text-base">
                      Suit No:{" "}
                      {property?.suit_number || searchDetails?.suit_number}
                    </div>
                    <div className="text-sm sm:text-base">
                      Date of Commencement:{" "}
                      {moment(
                        property?.date_of_commencement ||
                          searchDetails?.date_of_commencement,
                      ).format("MMM D, YYYY h:mma") || "-"}
                    </div>
                    <div className="text-sm sm:text-base">
                      Date of Disposal:{" "}
                      {searchDetails?.date_of_disposal ||
                      property?.date_of_disposal
                        ? moment(
                            searchDetails?.date_of_disposal ||
                              property?.date_of_disposal,
                          ).format("MMM D, YYYY h:mma")
                        : "-"}
                    </div>
                  </div>
                  <div className="text-sm sm:text-base break-words">
                    Nature of dispute:{" "}
                    {property?.nature_of_case || searchDetails?.nature_of_case}
                  </div>
                </div>

                <div className="text-center mt-6 sm:mt-10 mb-3 relative">
                  <h2 className="font-bold text-base sm:text-lg mb-2">
                    TO WHOM IT MAY CONCERN
                  </h2>

                  <p className="text-xs sm:text-sm text-center mb-2 leading-relaxed">
                    This certificate of Lis Pendens serves to inform all
                    potential buyers, lenders, and other interested parties that
                    the property above is the subject of litigation and any
                    transactions involving the property may be subject to the
                    outcome of the pending litigation. Transactions or dealings
                    with the property should be conducted with full awareness of
                    the ongoing litigation.
                  </p>

                  <p className="text-xs sm:text-sm text-left mb-3">
                    Search Date:{" "}
                    {moment(
                      searchDetails?.created_at !== null
                        ? searchDetails?.created_at
                        : property?.created_at,
                    ).format("YYYY-D-MM")}
                  </p>

                  <div className="flex flex-col sm:flex-row relative items-center mt-4 mb-4 w-full gap-4">
                    <div className="text-left w-full sm:w-auto mb-3">
                      <p className="font-bold text-sm -mb-5 sm:text-base ">
                        Signature:
                      </p>
                      <img
                        src={generalSettings?.acr_seal_path || SIGNATURE}
                        alt="Signature"
                        className="object-contain h-[80px] w-[80px] sm:h-[120px] sm:w-[120px]"
                      />
                      <div className="-mt-8">
                        <p className="text-sm sm:text-base">Nkiru Noble</p>
                        <p className="text-sm sm:text-base">ACR Litigation</p>
                      </div>
                    </div>

                    <div className="flex-shrink-0 absolute right-[40%]">
                      <img
                        src={generalSettings?.acr_stamp_path || STAMP}
                        alt="Stamp"
                        className="object-contain h-[80px] w-[80px] sm:h-[120px] sm:w-[120px]"
                      />
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm text-center mt-6 leading-relaxed">
                    For any further information or clarification, please contact
                    the court at Court 1 Anambra.{" "}
                    <span className="font-bold">
                      This certificate is issued in accordance with the legal
                      requirements and serves as a public notice of the ongoing
                      litigation affecting the described property as the search
                      date. This notice is for informational purposes only and
                      does not constitute legal advice.
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SearchDetails;
